import { createSlice } from '@reduxjs/toolkit';
//
import { dispatch } from '../store';
// slice
import { updateError } from './user';
// utils
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';

// -----------------------------------------------

const initialState = {
    isLoading: false,
    templates: null,
    currentTemplate: null,
    triggers: null,
    // #215 (I-NEW-2) — Bumped on any template mutation (create/update/delete/
    // archive/clone/revert). Consumers (e.g. useAttendedChecklistVisibility)
    // subscribe to this to refetch derived data without polling.
    templatesUpdatedAt: 0,
};

const slice = createSlice({
    name: 'taskTemplate',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
        },
        setTemplates(state, action) {
            state.isLoading = false;
            state.templates = action.payload;
        },
        setCurrentTemplate(state, action) {
            state.isLoading = false;
            state.currentTemplate = action.payload;
        },
        setTriggers(state, action) {
            state.isLoading = false;
            state.triggers = action.payload;
        },
        bumpTemplatesUpdatedAt(state) {
            state.templatesUpdatedAt = Date.now();
        },
    }
});

export default slice.reducer;

export const {
    reset,
    startLoading,
    setTemplates,
    setCurrentTemplate,
    setTriggers,
    bumpTemplatesUpdatedAt,
} = slice.actions;

export function getTemplates(practiceId, params) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/task-templates/?${params}`);
            if (response.status === 200) {
                dispatch(slice.actions.setTemplates(response.data));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getTemplate(practiceId, templateId) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/task-templates/${templateId}/`);
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentTemplate(response.data));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function createTemplate(practiceId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-templates/`, 'post', data);
            if (response.status === 201) {
                dispatch(slice.actions.bumpTemplatesUpdatedAt());
                dispatch(updateError({ success: true, message: 'Template created successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function updateTemplate(practiceId, templateId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-templates/${templateId}/`, 'patch', data);
            if (response.status === 200) {
                dispatch(slice.actions.bumpTemplatesUpdatedAt());
                dispatch(updateError({ success: true, message: 'Template updated successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function deleteTemplate(practiceId, templateId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-templates/${templateId}/`, 'delete');
            if (response.status === 204) {
                dispatch(slice.actions.bumpTemplatesUpdatedAt());
                dispatch(updateError({ success: true, message: 'Template deleted successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function archiveTemplate(practiceId, templateId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-templates/${templateId}/archive/`, 'post');
            if (response.status === 204) {
                dispatch(slice.actions.bumpTemplatesUpdatedAt());
                dispatch(updateError({ success: true, message: 'Template archived successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function cloneTemplate(practiceId, templateId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-templates/${templateId}/clone/`, 'post');
            if (response.status === 201) {
                dispatch(slice.actions.bumpTemplatesUpdatedAt());
                dispatch(updateError({ success: true, message: 'Template cloned — opening for edit' }));
                handleSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function applyTemplate(practiceId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/from-template/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Template applied successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

// -----------------------------------------------
// Version history (#125)
// Mirrors engagement-templates pattern: GET /history/ + POST /revert/.
// TODO BE: confirm `/api/v1/practices/<pid>/task-templates/<tid>/history/` and
// `/revert/` endpoints exist for task templates. If not, BE follow-up: add
// django-simple-history endpoints (parity with engagement-templates).
// FE handles 404 / empty gracefully — UI shows "No version history available".

export function getTaskTemplateHistory({ practiceId, templateId, callback }) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-templates/${templateId}/history/`);
            if (response.status === 200) {
                callback(response.data);
            } else {
                callback({ results: [] });
                if (response.status !== 404) {
                    dispatch(updateError({ message: 'Failed to load version history', success: false }));
                }
            }
        } catch (error) {
            callback({ results: [] });
            dispatch(updateError({ message: error?.message || 'Failed to load version history', success: false }));
        }
    };
}

// #216 (I-NEW-3) — Optimistic-concurrency guard for template revert.
// `expectedVersion` (history_id, version_number, or updated_at) is sent so the
// BE follow-up (#208) can reject the revert with 409 if the template was
// edited by someone else since the user opened the version-history surface.
// Until the BE ships, the extra fields are harmless metadata; the FE already
// surfaces the friendly 409 message when one is returned.
export function revertTaskTemplate({
    practiceId,
    templateId,
    historyId,
    expectedVersion,
    callback,
}) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const body = { history_id: historyId };
            if (expectedVersion !== undefined && expectedVersion !== null) {
                body.expected_version = expectedVersion;
            }
            const response = await callApi(
                `/api/v1/practices/${practiceId}/task-templates/${templateId}/revert/`,
                'post',
                body
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(slice.actions.bumpTemplatesUpdatedAt());
                dispatch(updateError({ message: 'Template reverted successfully', success: true }));
                if (callback) callback(response.data);
            } else if (response.status === 409) {
                const conflictMessage = 'This template was edited by someone else; refresh to see the latest version.';
                dispatch(updateError({ message: conflictMessage, success: false }));
                if (callback) callback({ success: false, conflict: true, message: conflictMessage });
            } else {
                dispatch(updateError({ message: 'Failed to restore version', success: false }));
                if (callback) callback({ success: false, message: response.data?.detail || 'Failed to restore version' });
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Failed to restore version', success: false }));
            if (callback) callback({ success: false, message: error?.message || 'Failed to restore version' });
        }
    };
}

export function getTriggers(practiceId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-triggers/`);
            if (response.status === 200) {
                dispatch(slice.actions.setTriggers(response.data));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function createTrigger(practiceId, data, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-triggers/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Trigger created successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function updateTrigger(practiceId, triggerId, data, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-triggers/${triggerId}/`, 'patch', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Trigger updated successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function deleteTrigger(practiceId, triggerId, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-triggers/${triggerId}/`, 'delete');
            if (response.status === 204) {
                dispatch(updateError({ success: true, message: 'Trigger deleted successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}
